import type { ManualIncentiveEditEntry } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';
import { INCENTIVE_TYPE_VALUES } from '@makemydeal/dr-dash-types';
import type { DeleteToggleResult, IncentiveFieldErrors, ValidationErrorsByIndex } from '../types';
import { FIELD_DISPLAY_NAMES, INCENTIVE_FIELD_NAMES, REQUIRED_FIELDS } from '../constants';

export const generateEditId = (): string => `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const getIncentiveKey = (inc: ManualIncentiveEditEntry): string => {
    if (inc.program && inc.code) {
        return `${inc.code}-${inc.program}`;
    }
    if (inc.code && inc.description) {
        return `${inc.code}-${inc.isStandardIncentive ? 'manufacturer' : 'program'}`;
    }
    return `${inc.name}-${inc.code || ''}-${inc.type}`;
};

/**
 * Removes the _editId field from an incentive for comparison purposes
 */
export const stripEditId = (incentive: ManualIncentiveEditEntry): Omit<ManualIncentiveEditEntry, '_editId'> => {
    const { _editId, ...rest } = incentive;
    return rest;
};

/**
 * Removes _editId fields from an array of incentives for comparison purposes
 */
export const stripEditIds = (incentives: ManualIncentiveEditEntry[]): Omit<ManualIncentiveEditEntry, '_editId'>[] => {
    return incentives.map(stripEditId);
};

const canonicalize = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(canonicalize);
    }
    if (value && typeof value === 'object') {
        return Object.keys(value as Record<string, unknown>)
            .sort()
            .reduce<Record<string, unknown>>((acc, key) => {
                const fieldValue = (value as Record<string, unknown>)[key];
                acc[key] = fieldValue === undefined ? null : canonicalize(fieldValue);
                return acc;
            }, {});
    }
    return value;
};

export const toComparableIncentives = (incentives: ManualIncentiveEditEntry[]): string[] =>
    stripEditIds(incentives)
        .map((entry) => JSON.stringify(canonicalize(entry)))
        .sort();

export const isIncentiveFormValid = (incentives: ManualIncentiveEditEntry[], isOptionalDeskingMode = false): boolean =>
    incentives.every((incentive: ManualIncentiveEditEntry) => {
        const typeValid = isOptionalDeskingMode || incentive.type !== '';
        const nameValid = isOptionalDeskingMode || incentive.name !== '';
        return (typeValid && nameValid) || incentive.deleted;
    });

export const isIncentiveFromProgram = (incentive: ManualIncentiveEditEntry): boolean => {
    return !!incentive.description && incentive.description !== incentive.name;
};

export const isProgramIncentiveOverride = (incentive: ManualIncentiveEditEntry): boolean =>
    isIncentiveFromProgram(incentive) && Boolean(incentive.manual);

export const hasProgramCode = (incentive: ManualIncentiveEditEntry): boolean =>
    incentive.code != null && String(incentive.code) !== '';

export const becameManual = (previous: ManualIncentiveEditEntry, updated: ManualIncentiveEditEntry): boolean =>
    !previous.manual && Boolean(updated.manual);

export const isConversionToCustomIncentive = (previous: ManualIncentiveEditEntry, updated: ManualIncentiveEditEntry): boolean =>
    becameManual(previous, updated) && hasProgramCode(previous);

export const isExemptFromCodeUniqueness = (
    current: ManualIncentiveEditEntry | undefined,
    other: ManualIncentiveEditEntry,
    isRestorationEnabled: boolean
): boolean => isRestorationEnabled && (Boolean(other.manual) || Boolean(current?.manual));

export const resolveManualFlag = (
    incentive: ManualIncentiveEditEntry,
    fieldName: keyof ManualIncentiveEditEntry,
    textValue: string | number
): boolean => {
    if (incentive.manual) {
        return true;
    }
    if (fieldName !== INCENTIVE_FIELD_NAMES.AMOUNT) {
        return false;
    }
    return Number(textValue) !== Number(incentive.originalAmount);
};

export const isIncentivesValid = (incentives: ManualIncentiveEditEntry[]): boolean => {
    const isRequiredFieldsValid = Boolean(incentives.length) && isIncentiveFormValid(incentives);

    const incentiveCodes = incentives.filter((i) => i.code).map((i) => i.code);
    const isCodesValid = new Set(incentiveCodes).size === incentiveCodes.length;

    return isRequiredFieldsValid && isCodesValid;
};

export const createNewManualIncentiveEntry = (): ManualIncentiveEditEntry => ({
    program: '',
    code: undefined,
    manual: true,
    name: '',
    description: '',
    type: INCENTIVE_TYPE_VALUES.customerCash.value,
    amount: 0,
    deleted: false,
    originalAmount: 0,
    isStandardIncentive: false,
    isNewIncentive: true,
    _editId: generateEditId()
});

export const isFieldEmpty = (value: string | number | undefined | null): boolean => {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
};

export const getRequiredFields = (isOptionalDeskingMode: boolean): (keyof ManualIncentiveEditEntry)[] => {
    if (isOptionalDeskingMode) {
        return [];
    }
    return REQUIRED_FIELDS;
};

export const validateRequiredFields = (
    incentive: ManualIncentiveEditEntry,
    updatedFieldName: keyof ManualIncentiveEditEntry,
    updatedFieldHasError: boolean,
    isOptionalDeskingMode: boolean
): IncentiveFieldErrors => {
    const errors: IncentiveFieldErrors = {};
    const requiredFields = getRequiredFields(isOptionalDeskingMode);

    requiredFields.forEach((fieldName) => {
        if (fieldName === updatedFieldName) {
            errors[fieldName] = updatedFieldHasError;
        } else {
            const value = incentive[fieldName] as string | number | undefined | null;
            errors[fieldName] = isFieldEmpty(value);
        }
    });

    return errors;
};

export const validateAllRequiredFields = (
    incentive: ManualIncentiveEditEntry,
    isOptionalDeskingMode: boolean
): IncentiveFieldErrors => {
    const errors: IncentiveFieldErrors = {};
    const requiredFields = getRequiredFields(isOptionalDeskingMode);

    requiredFields.forEach((fieldName) => {
        const value = incentive[fieldName] as string | number | undefined | null;
        errors[fieldName] = isFieldEmpty(value);
    });

    return errors;
};

export const hasAnyErrors = (errors: IncentiveFieldErrors): boolean => {
    return Object.values(errors).some(Boolean);
};

export const getUpdatedValidationErrors = (
    prevValidation: ValidationErrorsByIndex,
    incentiveIndex: number,
    rowErrors: IncentiveFieldErrors
): ValidationErrorsByIndex => {
    if (!hasAnyErrors(rowErrors)) {
        return Object.fromEntries(Object.entries(prevValidation).filter(([key]) => Number(key) !== incentiveIndex));
    }

    return {
        ...prevValidation,
        [incentiveIndex]: rowErrors
    };
};

export const formatValidationErrorMessage = (missingFieldNames: string): string => {
    return missingFieldNames
        ? `Please enter value for ${missingFieldNames} before continuing.`
        : 'Please enter value for the required field(s) before continuing.';
};

export const getMissingFieldNames = (validationErrors: ValidationErrorsByIndex): string => {
    const missingFields = new Set<string>();
    Object.values(validationErrors).forEach((rowErrors) => {
        Object.entries(rowErrors).forEach(([field, hasError]) => {
            if (hasError) {
                const displayName = FIELD_DISPLAY_NAMES[field as keyof ManualIncentiveEditEntry];
                if (displayName) {
                    missingFields.add(displayName);
                }
            }
        });
    });
    return Array.from(missingFields).join(', ');
};

export const getValidationErrorMessage = (validationErrors: ValidationErrorsByIndex): string => {
    const missingFieldNames = getMissingFieldNames(validationErrors);
    return formatValidationErrorMessage(missingFieldNames);
};

export const revalidateAllIncentives = (
    prevValidation: ValidationErrorsByIndex,
    incentives: ManualIncentiveEditEntry[],
    isOptionalDeskingMode: boolean,
    skipIndex?: number
): ValidationErrorsByIndex => {
    const updated = { ...prevValidation };
    incentives.forEach((incentive, index) => {
        if (index === skipIndex) return;
        if (incentive.deleted) {
            delete updated[index];
            return;
        }
        const rowErrors = validateAllRequiredFields(incentive, isOptionalDeskingMode);
        if (hasAnyErrors(rowErrors)) {
            updated[index] = rowErrors;
        } else {
            delete updated[index];
        }
    });
    return updated;
};

export const toggleDeleteAndCheckDuplicate = (
    currentIncentives: ManualIncentiveEditEntry[],
    index: number,
    isUniqueCodeFn: (currentIndex: number, currentCode: number | undefined, incentivesList?: ManualIncentiveEditEntry[]) => boolean
): DeleteToggleResult | null => {
    const incentiveAtIndex = currentIncentives[index];

    if (!incentiveAtIndex) {
        return null;
    }

    const updated = { ...incentiveAtIndex, deleted: !incentiveAtIndex.deleted };
    const nextIncentives = currentIncentives.map((incentive, currentIndex) => (currentIndex === index ? updated : incentive));

    const updatedCode = updated.code !== undefined && updated.code !== null ? Number(updated.code) : undefined;
    const hasDuplicate = updatedCode !== undefined ? !isUniqueCodeFn(index, updatedCode, nextIncentives) : false;

    return { nextIncentives, updated, hasDuplicate };
};
